import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { JsonLd, ORGANIZATION, SITE_URL } from "@/components/JsonLd";

/**
 * /travel-medical-records-app — the answer to the question assistants are
 * asked most about this product's category (MAR-009 §6).
 *
 * The weekly sweep of 23 October 2026 found ChatGPT answering "What app can I
 * use to keep my vaccination records and medical history with me while
 * travelling abroad?" from competitors' own product pages (syncmd.com/travel,
 * curapass.com, vaxy.app). Each of them states the use case in plain words on
 * one page. This is ours: the question as the title, the answer in the first
 * paragraph, and only what the plans actually include (`/api/stripe/pricing`).
 *
 * English only, like the FAQ's structured data: it is written for the
 * assistants' English index first. The prices are the live ones; change them
 * here when the catalogue changes.
 */

const FAQS = [
  {
    question: "Do I need to download HealthPass from an app store?",
    answer:
      "No. HealthPass runs in the browser on any phone or computer, and you can add it to your home screen like an app. It is not in the App Store or Google Play.",
  },
  {
    question: "Can a doctor abroad read my records without the app?",
    answer:
      "Yes. Your emergency card has a QR code. Scanning it opens the card in a browser, in English, German, French, Spanish or Portuguese, with no app and no login. You decide what the card shows, and every time it is opened is recorded.",
  },
  {
    question:
      "Can I store my yellow fever certificate and vaccination records?",
    answer:
      "Yes. Vaccination tracking and document storage for scans such as an International Certificate of Vaccination are part of the PLUS plan. The emergency card itself is free.",
  },
  {
    question: "Does it work without internet?",
    answer:
      "Offline access is part of PLUS and PREMIUM: your records stay available on the phone when you have no signal. Keep a printed copy of your emergency card as well, which works without a battery.",
  },
  {
    question: "Where is my data stored?",
    answer:
      "Data is encrypted in transit and at rest and hosted in Virginia, USA. GDPR applies; the controller is Centrafox Consulting LLC and its EU representative is INNOVIZR.TEAM UG.",
  },
  {
    question: "What does it cost?",
    answer:
      "The emergency card and basic trip planning are free. PLUS is $9 a month and PREMIUM $15 a month; for a single trip, PLUS is $25 and PREMIUM $39 for 90 days.",
  },
];

/** The plans as this page states them, in dollars, for the SoftwareApplication markup. */
const OFFERS = [
  { name: "FREE", price: 0 },
  { name: "PLUS, monthly", price: 9 },
  { name: "PREMIUM, monthly", price: 15 },
  { name: "PLUS, single trip of 90 days", price: 25 },
  { name: "PREMIUM, single trip of 90 days", price: 39 },
];

const KEEP = [
  {
    what: "Conditions, medications and allergies",
    why: "What a doctor needs before treating you, with generic drug names",
  },
  {
    what: "Vaccination records",
    why: "Boosters due, and certificates a border may ask for, such as yellow fever",
  },
  {
    what: "Emergency contacts and insurance",
    why: "Who to call, including your insurer's emergency line",
  },
  {
    what: "Documents",
    why: "Doctor's letters, prescriptions and discharge summaries as scans",
  },
];

export function TravelRecordsAppPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="An app for your vaccination records and medical history when you travel"
        description="HealthPass keeps your conditions, medications, allergies, vaccinations and emergency contacts in one place, with an emergency card a first responder abroad can read in five languages. Free to start, no app store needed."
        keywords="travel medical records app, vaccination record app travel, medical history app abroad, emergency medical card app, travel health app"
        url="https://travelhealthpass.app/travel-medical-records-app"
        canonical="https://travelhealthpass.app/travel-medical-records-app"
      />
      <JsonLd
        data={{
          "@type": "FAQPage",
          mainEntity: FAQS.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: { "@type": "Answer", text: faq.answer },
          })),
        }}
      />
      <JsonLd
        data={{
          "@type": "SoftwareApplication",
          name: "HealthPass",
          url: `${SITE_URL}/travel-medical-records-app`,
          applicationCategory: "HealthApplication",
          operatingSystem: "Web, Android, iOS (PWA)",
          description:
            "Keeps conditions, medications, allergies, vaccinations and emergency contacts in one place, with an emergency card a first responder abroad reads in five languages.",
          offers: OFFERS.map((offer) => ({
            "@type": "Offer",
            name: offer.name,
            price: offer.price.toFixed(2),
            priceCurrency: "USD",
          })),
          publisher: { "@id": ORGANIZATION["@id"] },
        }}
      />

      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white pb-16 pt-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-5xl font-bold mb-6">
            What app can I use to keep my vaccination records and medical
            history with me while travelling?
          </h1>
          <p className="text-lg md:text-xl text-blue-100">
            HealthPass keeps your conditions, medications, allergies,
            vaccinations and emergency contacts in one place on your phone, and
            puts the essentials on an emergency card with a QR code. A first
            responder abroad scans it and reads it in English, German, French,
            Spanish or Portuguese, with no app and no login. The card is free,
            and HealthPass runs in the browser, so there is nothing to download
            from an app store.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/traveler/register"
              className="bg-white text-blue-700 font-semibold px-6 py-3 rounded-lg hover:bg-blue-50 transition"
            >
              Create your free emergency card
            </Link>
            <a
              href="/blog/emergency-medical-id-card-travel"
              className="border border-white/70 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/10 transition"
            >
              What belongs on an emergency card
            </a>
            <a
              href="/emergency-medical-card"
              className="border border-white/70 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/10 transition"
            >
              Print a free card, no sign-up
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-14">
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            What to keep with you, and why
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-sm border border-gray-200 text-left">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3">Record</th>
                  <th className="px-4 py-3">Why you need it abroad</th>
                </tr>
              </thead>
              <tbody>
                {KEEP.map((row) => (
                  <tr key={row.what} className="border-t border-gray-200">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {row.what}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{row.why}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            How it works
          </h2>
          <ol className="list-decimal pl-6 space-y-3 text-gray-700">
            <li>
              Enter your conditions, medications, allergies and emergency
              contacts once. You choose which of them the emergency card shows.
            </li>
            <li>
              Plan a trip, and HealthPass lists the health requirements of your
              destination (PLUS), such as required or recommended vaccinations.
            </li>
            <li>
              Keep your vaccination records and scans of certificates and
              doctor&apos;s letters with the rest (PLUS).
            </li>
            <li>
              In an emergency, anyone can scan the QR code on your phone or on a
              printed card and read the essentials in their language.
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Free and paid plans
          </h2>
          <ul className="space-y-3 text-gray-700">
            <li>
              <strong>Free:</strong> emergency health data, the QR emergency
              card, basic trip planning, 5 medication reminders a month.
            </li>
            <li>
              <strong>PLUS, $9 a month:</strong> destination health
              requirements, vaccination tracking, 1 GB document storage, offline
              access, unlimited medication reminders.
            </li>
            <li>
              <strong>PREMIUM, $15 a month:</strong> travel companions,
              hospitals, pharmacies and clinics near your destination, a trip
              health summary for your own doctor, alerts when health advice for
              your destination changes, 10 GB storage.
            </li>
            <li>
              <strong>Single trip:</strong> PLUS for $25 or PREMIUM for $39, for
              90 days, with no subscription.
            </li>
          </ul>
          <p className="mt-4">
            <Link
              to="/pricing"
              className="text-blue-700 font-semibold hover:underline"
            >
              Compare the plans
            </Link>
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {FAQS.map((faq) => (
              <div key={faq.question}>
                <h3 className="text-lg font-semibold text-gray-900">
                  {faq.question}
                </h3>
                <p className="text-gray-700 mt-1">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Guides for travelling with a condition
          </h2>
          <ul className="list-disc pl-6 space-y-2 text-blue-700">
            <li>
              <a href="/blog/medication-abroad-what-documents-you-need">
                Medication abroad: the documents you need
              </a>
            </li>
            <li>
              <a href="/blog/insulin-on-plane-tsa-rules">
                Insulin on a plane: TSA rules
              </a>
            </li>
            <li>
              <a href="/blog/long-haul-flight-health-chronic-illness">
                Long-haul flights with a chronic illness
              </a>
            </li>
            <li>
              <a href="/blog/travel-insurance-pre-existing-conditions">
                Travel insurance with a pre-existing condition
              </a>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
